// Système de protection anti-bot pour EMDR Plus
import { useState, useEffect } from 'react'
import { logBotDetection, logAuthAttempt, logSuspiciousEmail, logRateLimit } from './securityLogger'

class BotProtection {
  constructor() {
    this.attempts = new Map()
    this.honeypotFields = new Set()
    this.startTime = Date.now()
  }

  // 1. Rate limiting par IP/session
  checkRateLimit(identifier = 'default') {
    const now = Date.now()
    const windowMs = 15 * 60 * 1000 // 15 minutes
    const maxAttempts = 3

    if (!this.attempts.has(identifier)) {
      this.attempts.set(identifier, [])
    }

    const attempts = this.attempts.get(identifier)
    
    // Nettoyer les anciennes tentatives
    const recentAttempts = attempts.filter(time => now - time < windowMs)
    this.attempts.set(identifier, recentAttempts)

    if (recentAttempts.length >= maxAttempts) {
      const nextAttemptTime = recentAttempts[0] + windowMs
      const waitTime = Math.ceil((nextAttemptTime - now) / 1000 / 60)
      
      // Logger le rate limit
      logRateLimit(identifier, {
        attempts: recentAttempts.length,
        waitTime
      })
      
      return {
        allowed: false,
        message: `Trop de tentatives. Réessayez dans ${waitTime} minute(s).`,
        waitTime
      }
    }

    // Enregistrer cette tentative
    recentAttempts.push(now)
    this.attempts.set(identifier, recentAttempts)

    return { allowed: true }
  }

  // 2. Validation d'email avancée
  validateEmail(email) {
    // Pattern de base
    const basicPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
    if (!basicPattern.test(email)) {
      logSuspiciousEmail(email, 'Format invalide')
      return { valid: false, reason: 'Format d\'email invalide' }
    }

    // Domaines suspects/temporaires
    const suspiciousDomains = [
      '10minutemail.com', 'tempmail.org', 'guerrillamail.com',
      'mailinator.com', 'yopmail.com', 'temp-mail.org',
      'throwaway.email', 'getnada.com', 'maildrop.cc',
      'example.com', 'test.com', 'fake.com'
    ]

    const domain = email.split('@')[1]?.toLowerCase()
    if (suspiciousDomains.includes(domain)) {
      logSuspiciousEmail(email, 'Domaine temporaire', { domain })
      return { valid: false, reason: 'Domaine email non autorisé' }
    }

    // Patterns suspects
    const suspiciousPatterns = [
      { pattern: /^[a-z]+\d+@/, reason: 'Lettres suivies de chiffres' },
      { pattern: /^\d+[a-z]*@/, reason: 'Commence par des chiffres' },
      { pattern: /test|fake|spam|temp|throw/i, reason: 'Mots suspects' },
      { pattern: /^(.)\1{3,}@/, reason: 'Caractères répétés' }
    ]

    for (const { pattern, reason } of suspiciousPatterns) {
      if (pattern.test(email)) {
        logSuspiciousEmail(email, reason)
        return { valid: false, reason: 'Format d\'email suspect' }
      }
    }

    return { valid: true }
  }

  // 3. Détection de comportement humain
  analyzeUserBehavior(formData) {
    const issues = []

    // Temps de remplissage trop rapide
    const fillTime = Date.now() - this.startTime
    if (fillTime < 2000) { // Moins de 2 secondes
      issues.push('Formulaire rempli trop rapidement')
    }

    // Pas de mouvement de souris enregistré
    if (!formData.hasMouseMovement) {
      issues.push('Aucun mouvement de souris détecté')
    }

    // Pas de focus sur les champs
    if (!formData.hasFocusEvents) {
      issues.push('Aucun événement de focus détecté')
    }

    return {
      suspicious: issues.length > 0,
      issues,
      score: Math.max(0, 100 - (issues.length * 30))
    }
  }

  // 4. Honeypot invisible
  createHoneypot() {
    const honeypotId = `field_${Math.random().toString(36).substr(2, 9)}`
    this.honeypotFields.add(honeypotId)
    
    return {
      id: honeypotId,
      style: {
        position: 'absolute',
        left: '-9999px',
        width: '1px',
        height: '1px',
        opacity: '0',
        tabIndex: '-1',
        'z-index': '-1'
      }
    }
  }

  // 5. Vérification honeypot
  checkHoneypot(formData) {
    for (const fieldId of this.honeypotFields) {
      if (formData[fieldId] && formData[fieldId].trim() !== '') {
        logBotDetection('Honeypot rempli', 'unknown', {
          fieldId,
          value: formData[fieldId]
        })
        return {
          isBot: true,
          reason: 'Champ honeypot rempli'
        }
      }
    }
    return { isBot: false }
  }

  // 6. Validation finale
  async validateSubmission(email, formData = {}) {
    const identifier = this.getIdentifier()
    
    // Logger la tentative d'authentification
    logAuthAttempt(email, false, { identifier })
    
    // Vérifier le rate limiting
    const rateLimitCheck = this.checkRateLimit(identifier)
    if (!rateLimitCheck.allowed) {
      return {
        success: false,
        error: rateLimitCheck.message,
        type: 'rate_limit'
      }
    }

    // Vérifier l'email
    const emailCheck = this.validateEmail(email)
    if (!emailCheck.valid) {
      return {
        success: false,
        error: emailCheck.reason,
        type: 'invalid_email'
      }
    }

    // Vérifier le honeypot
    const honeypotCheck = this.checkHoneypot(formData)
    if (honeypotCheck.isBot) {
      return {
        success: false,
        error: 'Erreur de validation',
        type: 'bot_detected'
      }
    }

    // Analyser le comportement
    const behaviorCheck = this.analyzeUserBehavior(formData)
    if (behaviorCheck.suspicious && behaviorCheck.score < 40) {
      logBotDetection('Comportement suspect', email, {
        issues: behaviorCheck.issues,
        score: behaviorCheck.score
      })
      return {
        success: false,
        error: 'Veuillez réessayer plus lentement',
        type: 'suspicious_behavior'
      }
    }

    // Validation réussie
    logAuthAttempt(email, true, { 
      identifier,
      score: behaviorCheck.score
    })
    
    return { success: true }
  }

  // Obtenir un identifiant unique pour le rate limiting
  getIdentifier() {
    // Combinaison de plusieurs facteurs pour identifier un utilisateur/session
    const factors = [
      navigator.userAgent,
      screen.width + 'x' + screen.height,
      new Date().toDateString(),
      sessionStorage.getItem('session_id') || this.generateSessionId()
    ]
    
    return this.simpleHash(factors.join('|'))
  }

  generateSessionId() {
    const sessionId = Math.random().toString(36).substr(2, 15)
    sessionStorage.setItem('session_id', sessionId)
    return sessionId
  }

  // Hash simple pour l'identifiant
  simpleHash(str) {
    let hash = 0
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i)
      hash = ((hash << 5) - hash) + char
      hash = hash & hash // Convertir en 32 bits
    }
    return Math.abs(hash).toString(36)
  }

  // Réinitialiser les protections (pour les tests)
  reset() {
    this.attempts.clear()
    this.honeypotFields.clear()
    this.startTime = Date.now()
  }
}

// Instance globale
export const botProtection = new BotProtection()

// Hook React pour l'intégration facile
export const useBotProtection = () => {
  const [userInteraction, setUserInteraction] = useState({
    hasMouseMovement: false,
    hasFocusEvents: false,
    startTime: Date.now()
  })

  useEffect(() => {
    let mouseMovements = 0
    let focusEvents = 0

    const handleMouseMove = () => {
      mouseMovements++
      if (mouseMovements > 5) {
        setUserInteraction(prev => ({ ...prev, hasMouseMovement: true }))
      }
    }

    const handleFocus = () => {
      focusEvents++
      setUserInteraction(prev => ({ ...prev, hasFocusEvents: true }))
    }

    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('focusin', handleFocus)

    return () => {
      document.removeEventListener('mousemove', handleMouseMove)
      document.removeEventListener('focusin', handleFocus)
    }
  }, [])

  return {
    userInteraction,
    createHoneypot: () => botProtection.createHoneypot(),
    validateSubmission: (email, additionalData = {}) => 
      botProtection.validateSubmission(email, { ...userInteraction, ...additionalData })
  }
} 